import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DownloadIcon from "@mui/icons-material/Download";
import Button from "@mui/material/Button";
import { ThemeProvider } from "@mui/material/styles";
import SvgIcon from "@mui/material/SvgIcon";
import Excel from "exceljs";
import FileSaver from "file-saver";

import Theme from "../themes/Theme";

import { SnackBar } from "../components/utils/alert";

export const ExportExcel = ({
  fileType,
  fileName,
  data = [],
  asyncData = null,
  columns,
  showHeader = true,
  delimiter = ",",
  rowDelimiter = "\n",
  textButton = "",
  iconDownload = false,
  disabled = false,
}) => {
  const [t] = useTranslation("global");
  const xlsx = "xlsx";
  const csv = "csv";
  const validType = [xlsx, csv];
  const noValidColumns = ["Detail", "Action", "Adjustment"];

  const [isDownload, setIsDownload] = useState(false);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [blobType, setBlobType] = useState("");
  const [dataCSV, setDataCSV] = useState(data);

  const exportFile = async (typeFile, nameFile, dataFile, dataColumns, showHeader) => {
    try {
      if (asyncData) {
        dataFile = await asyncData();
      }
      const [isValid, message, icon] = ValidateExcel(typeFile, nameFile, dataFile, dataColumns);
      if (!isValid) return SnackBar(t(message), icon);
      dataFile.forEach((v) => delete v.merchant_id);
      setDataCSV(dataFile);
      getHeaders(dataFile, dataColumns, showHeader);
      getBlobType(typeFile);
      setIsDownload(true);
    } catch (error) {
      SnackBar(t("Download.Message.Error") + error, "error");
    }
  };

  const ValidateExcel = (typeFile, nameFile, dataFile, dataColumns) => {
    let isValid = false;
    let icon = "warning";
    let message = "";

    if (typeFile === undefined || typeFile.trim().length === 0 || !validType.some((type) => type === typeFile))
      message = "Download.Message.NoType";
    else if (nameFile === undefined || nameFile.trim().length === 0) message = "Download.Message.NoName";
    else if (dataFile === undefined || dataFile.length === 0) message = "Download.Message.NoData";
    else if (dataColumns === undefined || dataColumns.length === 0) message = "Download.Message.NoColumns";
    else isValid = true;

    return [isValid, message, icon];
  };

  const getHeaders = (dataFile, dataColumns, showHeader) => {
    const dataHeaders = Object.keys(dataFile[0]);
    let headers = [];

    Object.values(dataColumns).forEach(function (column, index) {
      if (column !== "" && !noValidColumns.some((value) => column.includes(value)))
        if (showHeader) {
          headers.push({
            header: t(column),
            key: dataHeaders[index],
          });
        } else {
          headers.push({
            key: dataHeaders[index],
          });
        }
    });

    setFileHeaders(headers);
  };

  const getBlobType = (typeFile) => {
    let type;

    if (typeFile === xlsx) type = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    else type = "text/csv;charset=utf-8";

    setBlobType(type);
  };

  const download = (typeFile, nameFile, dataFile, headersFile, typeBlob) => {
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet(nameFile);

    worksheet.columns = headersFile;
    worksheet.addRows(dataFile);

    if (typeFile === xlsx) {
      workbook.xlsx
        .writeBuffer()
        .then((xlsxFile) => {
          const blobXLSX = new Blob([xlsxFile], { type: typeBlob });
          FileSaver.saveAs(blobXLSX, nameFile);
        })
        .catch((_) => {
          SnackBar(t("Download.Message.Error"), "error");
        });
    } else {
      workbook.csv
        .writeBuffer({
          formatterOptions: {
            delimiter: delimiter,
            rowDelimiter: rowDelimiter,
          },
        })
        .then((csvFile) => {
          const blobCSV = new Blob([csvFile], { type: typeBlob });
          FileSaver.saveAs(blobCSV, nameFile);
        })
        .catch((_) => {
          SnackBar(t("Download.Message.Error"), "error");
        });
    }
  };

  useEffect(() => {
    if (isDownload) download(fileType, fileName, dataCSV, fileHeaders, blobType);

    setIsDownload(false);
  }, [isDownload]);

  function iconButton(type) {
    let icon;

    if (type === xlsx)
      icon =
        "M17.3663 8.75057L15.8366 7.62015H15.821V4.79648H15.8201L10.9179 0L10.9158 0.00249194H1.55046V7.62702L0 8.75075L0.00918902 8.79186V15.1716H1.55044V20H15.8211V15.1712H17.362V13.18L17.3663 8.75057ZM3.8042 10.7084H4.38592C4.4247 10.7084 4.4532 10.7131 4.47142 10.7235C4.48949 10.734 4.50553 10.7505 4.51986 10.7742L5.12323 11.7694C5.12977 11.7538 5.13724 11.7379 5.1455 11.7219C5.15375 11.7054 5.16341 11.6893 5.17369 11.672L5.71677 10.7837C5.73219 10.758 5.74932 10.7388 5.76817 10.7265C5.7867 10.7142 5.80991 10.7081 5.83701 10.7081H6.39381L5.4995 12.05L6.41701 13.5223H5.83514C5.79636 13.5223 5.76505 13.5127 5.74107 13.493C5.71724 13.4739 5.69792 13.451 5.68391 13.4254L5.07276 12.3897C5.06778 12.4037 5.06186 12.4176 5.05532 12.4313C5.04862 12.4448 5.04239 12.4575 5.03585 12.4693L4.45772 13.4256C4.44075 13.4499 4.42128 13.4723 4.39854 13.4924C4.37595 13.5125 4.34714 13.5226 4.31241 13.5226H3.76917L4.69039 12.0757L3.8042 10.7084ZM8.50659 13.0493V13.5225H6.83667V10.7084H7.41839V13.0493H8.50659ZM10.1013 12.5409C10.0644 12.4973 10.0159 12.4603 9.95674 12.4307C9.89709 12.4009 9.82949 12.3752 9.75318 12.3539C9.67686 12.3324 9.59868 12.309 9.51847 12.2833C9.43826 12.2576 9.36023 12.2267 9.2836 12.1905C9.20744 12.1544 9.13938 12.1064 9.08004 12.0469C9.02039 11.9871 8.97258 11.9134 8.93582 11.8254C8.89875 11.7378 8.88022 11.6283 8.88022 11.4979C8.88022 11.3933 8.90093 11.2916 8.94252 11.1931C8.9841 11.0952 9.04453 11.0078 9.12474 10.9315C9.20495 10.8552 9.30354 10.7936 9.42035 10.7475C9.53731 10.7008 9.67157 10.6774 9.82297 10.6774C9.99226 10.6774 10.1491 10.7036 10.2932 10.7561C10.4374 10.8087 10.5593 10.8818 10.6589 10.9746L10.5153 11.2481C10.4969 11.2779 10.4787 11.2995 10.4598 11.3131C10.4411 11.3266 10.4171 11.3336 10.3871 11.3336C10.3587 11.3336 10.3279 11.3235 10.295 11.3034C10.2622 11.2833 10.2235 11.2613 10.1793 11.2384C10.1357 11.2149 10.0851 11.1936 10.0281 11.1735C9.97108 11.1534 9.90442 11.1433 9.82795 11.1433C9.69355 11.1433 9.59278 11.1735 9.52565 11.2335C9.45837 11.2936 9.42473 11.3721 9.42473 11.469C9.42473 11.5308 9.44326 11.5823 9.48018 11.6233C9.51709 11.6639 9.56568 11.6991 9.62549 11.7289C9.68576 11.7586 9.75382 11.7851 9.83029 11.8082C9.90661 11.8317 9.98463 11.8571 10.065 11.8849C10.1454 11.9128 10.2232 11.9455 10.2997 11.9829C10.376 12.0204 10.4442 12.0675 10.5045 12.1242C10.5648 12.1813 10.6131 12.2505 10.6498 12.3327C10.6867 12.4151 10.705 12.5149 10.705 12.6328C10.705 12.7606 10.6827 12.8805 10.638 12.9926C10.5936 13.1043 10.529 13.2021 10.4453 13.2853C10.3612 13.3686 10.2573 13.4345 10.1338 13.4824C10.0102 13.5299 9.87016 13.5539 9.71379 13.5539C9.62471 13.5539 9.53562 13.5447 9.44716 13.527C9.35854 13.5091 9.2735 13.4838 9.19127 13.4511C9.10903 13.4192 9.03178 13.3806 8.95952 13.3351C8.88694 13.2898 8.82371 13.2392 8.76935 13.1837L8.93989 12.9082C8.95422 12.8876 8.97291 12.8702 8.99643 12.8568C9.01995 12.8434 9.04424 12.8367 9.0701 12.8367C9.10498 12.8367 9.14174 12.8495 9.18068 12.8755C9.21946 12.9012 9.264 12.9295 9.31462 12.9605C9.36492 12.9915 9.42333 13.0203 9.48983 13.046C9.55665 13.0717 9.63623 13.0848 9.72968 13.0848C9.86518 13.0848 9.97031 13.0538 10.0444 12.9915C10.119 12.9297 10.1561 12.8372 10.1561 12.7141C10.1564 12.6425 10.138 12.5849 10.1013 12.5409L10.1013 12.5409ZM13.0201 13.5225C12.9813 13.5225 12.95 13.5128 12.9259 13.4932C12.9021 13.474 12.883 13.4511 12.8687 13.4256L12.2576 12.3898C12.2526 12.4039 12.2467 12.4177 12.2404 12.4314C12.2339 12.445 12.2272 12.4576 12.221 12.4694L11.6428 13.4257C11.626 13.45 11.6064 13.4724 11.5835 13.4925C11.5609 13.5126 11.5324 13.5227 11.4974 13.5227H10.9541L11.8755 12.0759L10.9889 10.7087H11.5706C11.6094 10.7087 11.638 10.7134 11.6561 10.7238C11.6742 10.7343 11.6902 10.7508 11.7045 10.7744L12.3079 11.7697C12.3144 11.7541 12.3219 11.7382 12.3302 11.7222C12.3384 11.7056 12.3481 11.6896 12.3583 11.6723L12.9016 10.784C12.917 10.7583 12.9341 10.7391 12.953 10.7268C12.9715 10.7145 12.9947 10.7084 13.0218 10.7084H13.5786L12.6843 12.0503L13.6015 13.5226L13.0201 13.5225ZM10.2287 0.689569L10.2238 5.47768L15.1335 5.48297V8.75395L2.23727 8.75411V0.689797L10.2287 0.689569ZM15.134 19.3125H2.23771V15.1715H15.134V19.3125Z";
    else if (type === csv)
      icon =
        "M17.3576 8.75096L15.8282 7.62047H15.8124V4.79692H15.8114L10.9093 0L10.9071 0.00248185H1.54121V7.62712L0 8.79236V15.1717H1.54109L1.54093 20H15.8118L15.8119 15.1717H17.3533V13.1738L17.3576 8.75096ZM5.6584 12.5507C5.70044 12.6691 5.75783 12.7686 5.83089 12.8502C5.90411 12.9318 5.98942 12.9937 6.08683 13.0354C6.1844 13.0775 6.28895 13.0984 6.40017 13.0984C6.46593 13.0984 6.52581 13.0953 6.57932 13.0888C6.63284 13.0823 6.68294 13.0713 6.7287 13.0559C6.77446 13.0406 6.81789 13.0205 6.85853 12.9956C6.89917 12.9714 6.94043 12.94 6.98169 12.9023C6.99596 12.8907 7.01086 12.8809 7.02637 12.8735C7.04188 12.8657 7.05879 12.8617 7.07678 12.8617C7.1092 12.8617 7.13634 12.8735 7.15821 12.8966L7.38934 13.1428C7.27548 13.2875 7.13433 13.3976 6.96556 13.4726C6.79679 13.5475 6.59591 13.585 6.3634 13.585C6.1526 13.585 5.9632 13.549 5.79507 13.4774C5.62693 13.4055 5.48422 13.3056 5.36633 13.1777C5.24875 13.0495 5.15816 12.8977 5.09488 12.7218C5.03144 12.5459 4.99964 12.354 4.99964 12.1457C4.99964 12.0061 5.01469 11.873 5.0454 11.7472C5.07564 11.6211 5.11985 11.5051 5.17818 11.3991C5.2365 11.2929 5.30646 11.1975 5.38883 11.1119C5.47088 11.0267 5.56364 10.954 5.66695 10.8939C5.77041 10.8336 5.88333 10.7878 6.00541 10.756C6.12748 10.7244 6.25809 10.7087 6.39614 10.7087C6.49976 10.7087 6.59764 10.7179 6.69009 10.7359C6.78253 10.7539 6.86893 10.7793 6.94897 10.8117C7.02932 10.8441 7.103 10.8829 7.1711 10.929C7.23888 10.9747 7.30031 11.025 7.3546 11.0793L7.15854 11.3469C7.1469 11.3638 7.13201 11.3787 7.11402 11.3915C7.09571 11.4047 7.07152 11.4109 7.04018 11.4109C7.009 11.4109 6.97798 11.4001 6.94618 11.378C6.91438 11.356 6.87576 11.3315 6.82984 11.3044C6.78377 11.2772 6.72623 11.2527 6.65704 11.2307C6.58786 11.2087 6.50022 11.1978 6.39412 11.1978C6.27623 11.1978 6.16843 11.2192 6.07008 11.2619C5.97174 11.3044 5.88751 11.3661 5.81678 11.4469C5.74651 11.5279 5.69176 11.6273 5.65282 11.7447C5.6142 11.8623 5.5945 11.9965 5.5945 12.1463C5.59527 12.2974 5.61621 12.4322 5.65841 12.5507L5.6584 12.5507ZM8.95676 12.5727C8.91984 12.529 8.8716 12.4921 8.81219 12.4623C8.75279 12.4325 8.68485 12.4071 8.60853 12.3855C8.53221 12.3641 8.45403 12.3408 8.37368 12.3148C8.29364 12.289 8.21531 12.258 8.13915 12.2215C8.06283 12.1856 7.99505 12.1378 7.93548 12.0781C7.87592 12.0185 7.82783 11.9447 7.79122 11.8569C7.75431 11.7691 7.73585 11.6599 7.73585 11.5291C7.73585 11.4244 7.75648 11.323 7.79805 11.2246C7.83947 11.1266 7.90012 11.0391 7.98047 10.9626C8.06051 10.8863 8.15901 10.825 8.27627 10.7785C8.39323 10.7318 8.5274 10.7087 8.67849 10.7087C8.84788 10.7087 9.0047 10.7348 9.14881 10.7873C9.29307 10.8396 9.41499 10.9125 9.51489 11.0054L9.37125 11.2787C9.35326 11.3085 9.33495 11.3301 9.31603 11.3437C9.29711 11.3575 9.27337 11.3642 9.24328 11.3642C9.21474 11.3642 9.18418 11.354 9.15114 11.334C9.11826 11.314 9.07979 11.2921 9.03558 11.269C8.99184 11.2459 8.94127 11.224 8.88434 11.204C8.82742 11.184 8.76071 11.1739 8.6844 11.1739C8.55007 11.1739 8.44909 11.204 8.38177 11.264C8.3146 11.3244 8.28109 11.4028 8.28109 11.4998C8.28109 11.5618 8.29955 11.6135 8.33647 11.6538C8.37339 11.6948 8.42163 11.7298 8.48197 11.7596C8.54231 11.7894 8.61025 11.8159 8.68657 11.839C8.76288 11.8621 8.8409 11.8879 8.9211 11.9158C9.00145 11.9436 9.07963 11.9761 9.15594 12.0137C9.23226 12.0512 9.30051 12.0984 9.3607 12.1553C9.42103 12.2122 9.46928 12.2816 9.50588 12.3639C9.5428 12.4461 9.56126 12.5459 9.56126 12.6636C9.56126 12.7917 9.53876 12.9115 9.49409 13.0235C9.44957 13.1353 9.38535 13.2327 9.30128 13.3163C9.21705 13.3995 9.11343 13.4654 8.99012 13.5132C8.86649 13.561 8.72643 13.585 8.57007 13.585C8.48088 13.585 8.39215 13.5759 8.30343 13.5579C8.2147 13.5396 8.12955 13.5144 8.04749 13.482C7.96528 13.4497 7.88803 13.4111 7.81575 13.3657C7.74331 13.3205 7.67987 13.2701 7.62558 13.2144L7.79636 12.9389C7.81063 12.9183 7.82924 12.9011 7.85267 12.8878C7.87578 12.874 7.90044 12.8673 7.9265 12.8673C7.96141 12.8673 7.99817 12.8802 8.03694 12.9062C8.07588 12.932 8.1204 12.9605 8.17081 12.9914C8.22122 13.0226 8.2797 13.0509 8.34656 13.0768C8.4131 13.1026 8.49314 13.1158 8.58605 13.1158C8.72162 13.1158 8.82695 13.0846 8.90109 13.0225C8.97539 12.9607 9.01278 12.8682 9.01278 12.7452C9.01215 12.6742 8.99339 12.6169 8.95678 12.5727L8.95676 12.5727ZM11.3828 13.5543H10.8554L9.7226 10.7399H10.192C10.2424 10.7399 10.2833 10.7518 10.3151 10.7757C10.3468 10.7995 10.3704 10.8305 10.3859 10.8679L10.9947 12.4894C11.0178 12.5501 11.0409 12.6165 11.0636 12.6883C11.0862 12.7601 11.1065 12.8355 11.1247 12.9145C11.1414 12.8358 11.16 12.7604 11.1807 12.6883C11.2016 12.6166 11.2233 12.5504 11.2468 12.4894L11.852 10.8679C11.8652 10.8354 11.8878 10.8063 11.9209 10.7797C11.9541 10.7532 11.9949 10.7399 12.0444 10.7399H12.5156L11.3828 13.5543ZM10.22 0.689982L10.2154 5.47817L15.1251 5.48375V8.75467H2.2286V0.690046L10.22 0.689982ZM15.125 19.3134H2.22888V15.1721H15.1254V19.3134H15.125Z";

    return icon;
  }

  return (
    <ThemeProvider theme={Theme}>
      <Button
        disabled={disabled}
        style={{ borderRadius: "8px" }}
        variant="outlined"
        startIcon={
          iconDownload ? (
            <DownloadIcon />
          ) : (
            <SvgIcon viewBox="0 0 18 20">
              <path d={iconButton(fileType)} />
            </SvgIcon>
          )
        }
        onClick={(_) => exportFile(fileType, fileName, data, columns, showHeader)}
        className="action-button-outlined-secondary"
      >
        {textButton == "" ? t("Buttons.Export", { fileType: fileType }) : textButton}
      </Button>
    </ThemeProvider>
  );
};
